import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Route-level scroll behaviour for the app.
 *
 *  - Navigating to a new pathname (e.g. "/" → "/faq") always lands the user
 *    at the top of the destination page, matching how a real website feels.
 *  - When a link contains an in-page hash (e.g. "/#pricing" or same-page
 *    "#pricing"), we scroll to that section INSTEAD of the top, so anchor
 *    links keep working. If the target element doesn't exist yet (React
 *    hasn't mounted the section on first paint) we retry once on the next
 *    animation frame — this handles the very first navigation to a page
 *    that itself lazy-renders the anchor target.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const scrollToAnchor = () => {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return true;
        }
        return false;
      };
      if (scrollToAnchor()) return;
      // Section not mounted yet on first render — try once more next frame.
      const raf = window.requestAnimationFrame(() => scrollToAnchor());
      return () => window.cancelAnimationFrame(raf);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}
